import type { TenantId } from '@utils/tenant-utils'
import type {
  AddressCollectionModel,
  SummaryAddressCollectionModel,
} from '../Common/AddressCollectionModel'
import type { MailAddressModel } from '../Common/MailAddressModel'
import type { NameModel } from '../Common/NameModel'
import type { PhoneCollectionModel } from '../Common/PhoneCollectionModel'
import type { PhoneNumberModel } from '../Common/PhoneNumberModel'
import type { AdditionalInfoLineModel } from './AdditionalInfo'
import type { ContactPersonModel, SummaryContactPersonModel } from './ContactPerson'
import type { ContactPreferenceModel } from './ContactPreference'

// Backend DTO types matching the C# structures (from swagger.json)

export const IdentificationNumberTypeValues = {
  Ssn: 0,
  OrganizationNumber: 1,
  Fictitious: 2,
} as const

//TODO: alling enum int values with backend
export type IdentificationNumberTypeModel = 'Ssn' | 'OrganizationNumber' | 'Fictitious' | 0 | 1 | 2

export interface OrganizationTypeModel {
  code: string
  description: string
}

export interface IdentificationNumberModel {
  type: IdentificationNumberTypeModel
  number: string
  organizationType?: OrganizationTypeModel
}

export interface ForestOwnerGroupModel {
  name: string
  number: string
}

export interface OperatingOrganizationModel {
  name: string
  organizationNumber: string
}

export interface PersonNameDto {
  firstName: string
  lastName: string | null
}

export interface ForestManagerModel {
  employeeNumber: string
  name: string
  entraUserId: string
}

export interface MagazineSubscriptionModel {
  subscriptionId: string
  pricePlan: string
  numberOfCopies: number
  fromDate: string
  toDate?: string | null
}

export interface PostForestOwnerSubscriptionRequest {
  pricePlan: string
  numberOfCopies: number
  fromDate: string
  toDate?: string
}

export interface CreatedForestOwnerResourceModel {
  id: string
}

export interface PostForestOwnerActivateRequest {
  wasWoodSentBefore: boolean
}

export const ForestOwnerStatusModelValues = {
  None: 0,
  Draft: 1,
  Active: 2,
} as const

export type ForestOwnerStatusModel =
  | (typeof ForestOwnerStatusModelValues)[keyof typeof ForestOwnerStatusModelValues]
  | 'None'
  | 'Draft'
  | 'Active'

export interface UpdateForestOwnerStatusRequest {
  status: ForestOwnerStatusModel
  wasWoodSentBefore?: boolean | null
}

export interface UpdateMagazineSubscriptionRequest {
  pricePlan: string
  numberOfCopies: number
  fromDate: string
  toDate?: string
}

export interface UnregisteredCooperativeMembershipModel {
  cooperativeMembershipId: string
  membershipEndDate: string
  unregisterReason: string
}

export interface UploadPowerOfAttorneyModel {
  url: string
}

export const ForestOwnerTypeModelValues = {
  None: 0,
  Private: 1,
  Organization: 2,
  Partnership: 3,
} as const

export type ForestOwnerTypeModel =
  | (typeof ForestOwnerTypeModelValues)[keyof typeof ForestOwnerTypeModelValues]
  | 'None'
  | 'Private'
  | 'Organization'
  | 'Partnership'

export interface ForestOwnerSummaryModel {
  id: string
  forestOwnerType: ForestOwnerTypeModel
  name: NameModel
  addresses?: SummaryAddressCollectionModel
  contactPerson?: SummaryContactPersonModel
  cooperativeMembershipId?: string | null
  birthDate?: string | null
}

export interface ForestOwnerModel {
  id: string
  forestOwnerType: ForestOwnerTypeModel
  name: NameModel
  cooperativeMembershipId?: string | null
  birthDate?: string | null
  etag?: string
  email?: string | null
  customerNumber: string
  phone?: PhoneCollectionModel | null
  addresses?: AddressCollectionModel | null
  remark: string
  forestOwnerGroup?: ForestOwnerGroupModel | null
  identificationNumber?: IdentificationNumberModel | null
  bankAccountNumber?: string
  contactPreferences?: ContactPreferenceModel[] | null
  additionalInfoLines?: AdditionalInfoLineModel[] | null
  magazineSubscription?: MagazineSubscriptionModel | null
  contactPerson?: ContactPersonModel | null
  forestManager?: ForestManagerModel | null
  createdAt: string
  operatingOrganization?: OperatingOrganizationModel | null
  unregisteredCooperativeMembership?: UnregisteredCooperativeMembershipModel | null
}

export interface ShareHolderOwnership {
  paidShareCapital: number
  additionalCapital: number
  deRegistrationDate?: string
  deRegistrationReason?: string
  association?: string
  shareAccount?: string
}

export interface GetForestOwnersParams {
  tenantId?: TenantId
  pageSize?: number
  continuationToken?: string
  searchTerm?: string
  sort?: string
  onlyShareholders?: boolean
  forestOwnerType?: ForestOwnerTypeModel
}

export interface GetForestOwnerParams {
  id?: string
}

export interface ForestOwnerProjectionModel {
  title: string
  description: string
  createdAt: string
  changedByUserId: string
  changedByUserName: string | null
  action: string
  source: string
  propertyChangedName?: string | null
  valueFrom?: string | null
  valueTo?: string | null
}

export type ChangeType = 'Adresseopplysninger' | 'Kontaktopplysninger' | 'Andelshistorikk'

export interface UpdateForestOwnerRemarkRequest {
  remark?: string | null
}

export interface SetForestOwnerContactPreferencesRequest {
  contactPreferences: ContactPreferenceModel[]
}

export interface UpdateForestOwnerRequest {
  name?: NameModel | null
  email?: string | null
  address?: MailAddressModel | null
  mobilePhone?: PhoneNumberModel | null
  landlinePhone?: PhoneNumberModel | null
  contactPerson?: ContactPersonModel | null
  identificationNumber?: IdentificationNumberModel | null
  operatingOrganization?: OperatingOrganizationModel | null
  forestOwnerGroupNumber?: string | null
  bankAccountNumber?: string | null
  birthDate?: string | null
}

export interface ChangeForestOwnerToOrganizationRequest {
  forestOwnerType: Extract<ForestOwnerTypeModel, 'Organization'>
  name: NameModel
  email?: string | null
  address: MailAddressModel
  mobilePhone?: PhoneNumberModel | null
  landlinePhone?: PhoneNumberModel | null
  contactPerson?: ContactPersonModel | null
  identificationNumber: IdentificationNumberModel
  operatingOrganization?: OperatingOrganizationModel | null
  forestOwnerGroupNumber?: string | null
  bankAccountNumber?: string | null
}

export interface CreateForestOwnerResponse {
  id: string
  etag: string
  contactPreferences?: ContactPreferenceModel[]
}

export interface CreateForestOwnerRequest {
  tenantId: string
  name: NameModel
  email: string | null
  address: MailAddressModel
  mobilePhone?: PhoneNumberModel | null
  landlinePhone?: PhoneNumberModel | null
  contactPerson?: ContactPersonModel | null
  forestOwnerGroupNumber?: string | null
  identificationNumber?: IdentificationNumberModel | null
  birthDate?: string | null
  bankAccountNumber?: string | null
  operatingOrganization?: OperatingOrganizationModel | null
  forestManager?: ForestManagerModel | null
  forestOwnerType: ForestOwnerTypeModel
}
